import { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Chip,
    CircularProgress,
    IconButton,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import GroupIcon from '@mui/icons-material/Group';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../app/store';
import { getSearchResults } from '../../../../app/features/sessions/sessionSearch/sliceSearch';
import { format } from 'date-fns';
import { Group, User } from '../../../../app/#interfaces/interfaces';
import { MemberGroup } from '../../../../app/features/sessions/sessionGroups/sessionGroups';
import { GroupDetailsModal } from '../../../../app/components/modal/groupModal/GroupDetailsModal';
import { UserAvatar } from '../../../../app/components/userAvatar/UserAvatar';
import { useNavigate } from 'react-router-dom';
import * as REQUEST_REQUIREMENTS from '../../../../app/api/requestRequirements';

const BG_SECTION     = '#1c1c24';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

interface GroupsSearch {
    searchType: string;
    query: string | null;
}

export function GroupsSearch({ searchType, query }: GroupsSearch) {
    const dispatch = useDispatch<AppDispatch>();
    const authHeaders = useSelector((state: RootState) => state.session.authHeaders);
    const [groupsSearchPage, setGroupsSearchPage] = useState(1);
    const [isFetching, setIsFetching] = useState(false);
    const groups = useSelector((state: RootState) => state.sessionSearch.groups);
    const loadingMoreResults = useSelector((state: RootState) => state.sessionSearch.loadingMoreResults);
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [groupSelectedToShowDetails, setGroupSelectedToShowDetails] = useState<Group>();
    const [memberGroupSelected, setMemberGroupSelectedToShowDetails] = useState<MemberGroup>();
    const navigate = useNavigate();

    useEffect(() => {
        async function fetchGroupsData() {
            if (authHeaders && searchType && query && !isFetching) {
                setIsFetching(true);
                await dispatch(
                    getSearchResults({
                        headers: authHeaders,
                        routeParams: {
                            queryType: searchType,
                            query: query,
                            page: groupsSearchPage,
                        },
                    })
                );
                setIsFetching(false);
            }
        }

        fetchGroupsData();
    }, [authHeaders, searchType, query, groupsSearchPage, dispatch]);

    useEffect(() => {
        function handleScroll() {
            if (
                window.innerHeight + document.documentElement.scrollTop !==
                    document.documentElement.offsetHeight ||
                isFetching
            )
                return;
            setGroupsSearchPage((prevPage) => prevPage + 1);
        }

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isFetching]);

    function handleFormatDate(date: any) {
        const originalDate = new Date(date);
        const formattedDate = format(originalDate, 'dd MMM yyyy HH:mm:ss');
        return formattedDate;
    }

    async function handleNavigateEnterGroup() {
        if (groupSelectedToShowDetails) {
            const PRIVATE_ROUTES = REQUEST_REQUIREMENTS.handlePrivateRoutes({
                ROUTE_PARAMS: {
                    userNickname: memberGroupSelected?.user.nickname,
                    groupId: groupSelectedToShowDetails.id,
                },
            });

            navigate(PRIVATE_ROUTES.GROUP_UPDATE_SHOW);
        }
    }

    async function handleNavigationUserProfile(userTarget: User) {
        if (userTarget) {
            const routeParams = {
                userNickname: userTarget.userMoreInfo?.nickname,
            };

            const PRIVATE_ROUTES = await REQUEST_REQUIREMENTS.handlePrivateRoutes({
                ROUTE_PARAMS: routeParams,
            });

            return navigate(PRIVATE_ROUTES.USER);
        }
    }

    return (
        <Box>
            {showGroupModal && groupSelectedToShowDetails && (
                <GroupDetailsModal
                    group={groupSelectedToShowDetails}
                    open={setShowGroupModal}
                    enterGroup={handleNavigateEnterGroup}
                />
            )}

            {groups.length > 0 &&
                groups.map((item, index) => {
                    const visibleGenres = item.groupMusicalGenres?.slice(0, 4) ?? [];
                    const extraGenres = (item.groupMusicalGenres?.length ?? 0) - visibleGenres.length;

                    return (
                        <Box
                            key={index}
                            sx={{
                                bgcolor: BG_SECTION,
                                border: BORDER_SUBTLE,
                                borderRadius: '10px',
                                mb: 1.5,
                                p: 2,
                            }}
                        >
                            {/* Topo */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 1.5,
                                    gap: 1,
                                }}
                            >
                                {/* Nome + membros */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            color: TEXT_PRIMARY,
                                            fontWeight: 700,
                                            fontSize: 15,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {item.name}
                                    </Typography>
                                    <Typography sx={{ color: TEXT_SECONDARY, fontSize: 12, mt: 0.25 }}>
                                        Members: {item.nMembers}
                                    </Typography>
                                </Box>

                                {/* Avatar + nickname do criador */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        flexShrink: 0,
                                    }}
                                >
                                    <UserAvatar src={item.creator?.userMoreInfo?.avatarUrl} size={32} />
                                    <Typography
                                        onClick={() => handleNavigationUserProfile(item)}
                                        sx={{
                                            color: TEXT_SECONDARY,
                                            fontSize: 13,
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            '&:hover': { textDecoration: 'underline', color: TEXT_PRIMARY },
                                        }}
                                    >
                                        {item.creator?.nickname}
                                    </Typography>
                                </Box>

                                {/* Botão de detalhes */}
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        setShowGroupModal(true);
                                        setGroupSelectedToShowDetails(item);
                                    }}
                                    sx={{
                                        color: TEXT_SECONDARY,
                                        flexShrink: 0,
                                        '&:hover': { color: TEXT_PRIMARY, bgcolor: 'rgba(255,255,255,0.06)' },
                                    }}
                                >
                                    <InfoOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Box>

                            {/* Descrição */}
                            {item.description && (
                                <Typography
                                    sx={{
                                        color: TEXT_SECONDARY,
                                        fontSize: 13,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        mb: 1.5,
                                        display: '-webkit-box',
                                        WebkitLineClamp: 4,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {item.description}
                                </Typography>
                            )}

                            {/* Géneros musicais */}
                            {visibleGenres.length > 0 && (
                                <Box>
                                    <Typography
                                        sx={{
                                            color: TEXT_SECONDARY,
                                            fontSize: 11,
                                            mb: 0.5,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.08em',
                                        }}
                                    >
                                        Musical Genres
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {visibleGenres.map((groupMusicalGenre: any, i: number) => (
                                            <Chip
                                                key={i}
                                                label={groupMusicalGenre.musicGenre.description}
                                                size="small"
                                                sx={{
                                                    bgcolor: 'rgba(255,255,255,0.08)',
                                                    color: TEXT_PRIMARY,
                                                    fontSize: 11,
                                                    height: 22,
                                                }}
                                            />
                                        ))}
                                        {extraGenres > 0 && (
                                            <Chip
                                                label={`+${extraGenres}`}
                                                size="small"
                                                sx={{
                                                    bgcolor: 'rgba(255,255,255,0.08)',
                                                    color: TEXT_PRIMARY,
                                                    fontSize: 11,
                                                    height: 22,
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    );
                })}

            {/* Estado vazio */}
            {groups.length === 0 && !loadingMoreResults && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        mt: 8,
                        gap: 2,
                    }}
                >
                    <GroupIcon sx={{ fontSize: 56, color: TEXT_SECONDARY }} />
                    <Typography sx={{ color: TEXT_SECONDARY, fontSize: 16 }}>
                        Nothing to show
                    </Typography>
                </Box>
            )}

            {/* Loading */}
            {loadingMoreResults && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress sx={{ color: BRAND_RED }} />
                </Box>
            )}
        </Box>
    );
}
